import { Box, Button, Checkbox } from '@mui/material';
import { UserData } from '../../../data_layer/user/UserData';
import { FunctionComponent, useState, useEffect } from 'react';
import { ErrorPageNoAdmin } from '../../error/ErrorPageNoAdmin';
import { ApiObjBaseData } from '../../../api/object/base_data/ApiObjBaseData';
import { ApiObjAdminBaseData } from '../../../api/object/admin/ApiObjAdminBaseData';
import { ApiObjAdminPhoneNumberSource } from '../../../api/object/admin/ApiObjAdminPhoneNumberSource';
import { api_admin_user_get_phone_number_sources } from '../../../api/endpoint/admin/user/phone_number_source/api_admin_user_get_phone_number_sources';
import { api_admin_user_save_phone_number_sources } from '../../../api/endpoint/admin/user/phone_number_source/api_admin_user_save_phone_number_sources';

type Props = {
    baseData: ApiObjBaseData,
    userData: UserData,
    adminBaseData: ApiObjAdminBaseData,
    otherUserId: number,
}

export const AdminUserTabPhoneNumberSources: FunctionComponent <Props> = ({
    baseData,
    userData,
    adminBaseData,
    otherUserId
}) => {

    if (!userData.isLoggedInAndAdmin()) {
        return <ErrorPageNoAdmin />;
    }

    const [phoneNumberSources, setPhoneNumberSources] = useState <Array<ApiObjAdminPhoneNumberSource>> ([]);
    const [mapSelectedSources, setMapSelectedSources] = useState <Map<number, number>> (new Map());
    const [isLoading, setIsLoading] = useState <boolean> (true);
    const [isSaving, setIsSaving] = useState <boolean> (false);
    const isActive = !isLoading && !isSaving;

    // ======================================================================
    // === Setup
    // ======================================================================

    useEffect(() => {

        setIsLoading(true);
        api_admin_user_get_phone_number_sources(otherUserId)
            .then((result) => {
                setPhoneNumberSources(result);
                const newMap = new Map();
                for (const item of result) {
                    if (item.is_enabled_for_user) {
                        newMap.set(item.id, item.id);
                    }
                }
                setMapSelectedSources(newMap);
                setIsLoading(false);
            });

    }, [otherUserId]);

    // ======================================================================
    // === Actions
    // ======================================================================
  
    const actionSaveSources = () => {

        setIsSaving(true);

        const sourceIds : Array<number> = [];
        mapSelectedSources.forEach((value: number, key: number) => {
            sourceIds.push(key);
        });

        api_admin_user_save_phone_number_sources(otherUserId, sourceIds)
            .then((result) => {
                setPhoneNumberSources(result);
                const newMap = new Map();
                for (const item of result) {
                    if (item.is_enabled_for_user) {
                        newMap.set(item.id, item.id);
                    }
                }
                setMapSelectedSources(newMap);
                setIsSaving(false);

            }).catch((err) => {
                console.error("Could not save phone number sources");
                console.error(err);
                // Skip to set isSaving to fails to make error obvious.
            });
    }

    const actionOnCheckboxChange = (numberSourceId: number, event: React.ChangeEvent<HTMLInputElement>) => {
        const isChecked = event.target.checked;
        const newMap = new Map(mapSelectedSources);
        if (isChecked) {
            newMap.set(numberSourceId, numberSourceId);
        } else {
            newMap.delete(numberSourceId);
        }
        setMapSelectedSources(newMap);
    }
    
    // ======================================================================
    // === Render
    // ======================================================================

    const renderList = () : JSX.Element => {

        const elements : Array<JSX.Element> = [];
        for (const item of phoneNumberSources) {
            elements.push(renderItem(item));
        }
        
        return (
            <Box>
                {elements}
            </Box>
        )
    }

    const renderItem = (numberSource: ApiObjAdminPhoneNumberSource) : JSX.Element => {

        const isSelected = mapSelectedSources.has(numberSource.id);

        let typeString = '';
        switch (numberSource.source_type) {
            case ApiObjAdminPhoneNumberSource.SOURCE_TYPE_1_BASE:
                typeString = 'Baskälla';
                break;
            case ApiObjAdminPhoneNumberSource.SOURCE_TYPE_2_OPTIONAL:
                typeString = 'Premium för alla';
                break;
            case ApiObjAdminPhoneNumberSource.SOURCE_TYPE_3_OPTIONAL_PREMIUM:
                typeString = 'Premium exklusiv';
                break;
        }

        return (
            <Box
                key={numberSource.id}
                display={'flex'}
                flexDirection={'row'}
                alignContent={'center'}>
                    <Box>
                        <Checkbox
                            checked={isSelected}
                            disabled={!isActive}
                            color={'secondary'}
                            onClick={(event) => { event.stopPropagation(); }}
                            onChange={(event) => { actionOnCheckboxChange(numberSource.id, event) }}/>
                    </Box>
                    <Box display={'flex'} flexDirection={'row'} alignItems={'center'} width={'300px'}>
                        {numberSource.name}
                    </Box>
                    <Box display={'flex'} flexDirection={'row'} alignItems={'center'} width={'200px'}>
                        {typeString}
                    </Box>
                    <Box display={'flex'} flexDirection={'row'} alignItems={'center'} width={'200px'}>
                        {'Prio '+numberSource.prio}
                    </Box>
            </Box>
        );
    }

    if (isLoading) {
        return (<Box padding={'14px'}>Laddar...</Box>);
    }

    return (
        <Box padding={'14px'} display={'flex'} flexDirection={'column'}>
            {renderList()}
            <Box paddingTop={'15px'}>
                <Button variant='contained' color='secondary' onClick={actionSaveSources} disabled={!isActive}>
                    Spara
                </Button>
            </Box>
        </Box>
    );
}
